import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

interface SaveConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  changedFields: string[];
  hasChanges: boolean;
}

export const SaveConfirmationModal: React.FC<SaveConfirmationModalProps> = ({
  open,
  onClose,
  onSave,
  changedFields,
  hasChanges,
}) => {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!note.trim()) {
      setError('A note describing your changes is required.');
      return;
    }

    onSave(note.trim());
    setNote('');
    setError('');
  };

  const handleClose = () => {
    setNote('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Changes Detected - Note Required
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have made changes to this subscription. A note is required to explain what was modified before saving.
          </Alert>

          {changedFields.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Changed fields:
              </Typography>
              <List dense>
                {changedFields.map((field, index) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemText
                      primary={field}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <TextField
            label="Explain the changes made *"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (error) setError('');
            }}
            multiline
            minRows={3}
            fullWidth
            placeholder="Describe what you changed and why..."
            error={!!error}
            helperText={error}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!note.trim()}
        >
          Save with Note
        </Button>
      </DialogActions>
    </Dialog>
  );
};
